import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Typography,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  Button,
  Checkbox,
  Toolbar,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Tooltip,
} from '@mui/material';
import {
  MoreVert,
  Edit,
  Phone,
  Visibility,
  Assignment,
  Search,
  FilterList,
  Delete,
  FileDownload,
  Comment,
  Send,
  Add,
  Schedule,
  AccessTime,
} from '@mui/icons-material';
import { format } from 'date-fns';

const LeadsTable = ({ 
  leads = [], 
  loading = false, 
  onAssignLead,
  onEditLead,
  onCallLead,
  onDeleteLead,
  onAddComment,
  onFollowUpLead,
  onScheduleCall,
  totalCount = 0,
  page = 0,
  pageSize = 20,
  onPageChange,
  onPageSizeChange,
  onFilterChange,
  onBulkAssign,
  onExportLeads,
}) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [selected, setSelected] = useState([]);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [selectedLeadForComment, setSelectedLeadForComment] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [isInternal, setIsInternal] = useState(true);
  const [commentLoading, setCommentLoading] = useState(false);

  const handleMenuOpen = (event, lead) => {
    setAnchorEl(event.currentTarget);
    setSelectedLead(lead);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedLead(null);
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': 'primary',
      'warm': 'info',
      'hot': 'warning',
      'followup': 'warning',
      'ipd_done': 'success',
      'opd_done': 'success',
      'converted': 'success',
      'dnp': 'error',
      'dead': 'error'
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      urgent: 'error',
      high: 'warning',
      medium: 'info',
      low: 'default',
    };
    return colors[priority] || 'default';
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    onFilterChange?.({
      search: value,
      status: statusFilter,
      priority: priorityFilter,
      source: sourceFilter,
    });
  };

  const handleStatusFilterChange = (event) => {
    const value = event.target.value;
    setStatusFilter(value);
    onFilterChange?.({
      search: searchTerm,
      status: value,
      priority: priorityFilter,
      source: sourceFilter,
    });
  };

  const handlePriorityFilterChange = (event) => {
    const value = event.target.value;
    setPriorityFilter(value);
    onFilterChange?.({
      search: searchTerm,
      status: statusFilter,
      priority: value,
      source: sourceFilter,
    });
  };

  const handleSourceFilterChange = (event) => {
    const value = event.target.value;
    setSourceFilter(value);
    onFilterChange?.({
      search: searchTerm,
      status: statusFilter,
      priority: priorityFilter,
      source: value,
    });
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setPriorityFilter('');
    setSourceFilter('');
    onFilterChange?.({
      search: '',
      status: '',
      priority: '',
      source: '',
    });
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = leads.map((lead) => lead.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleCheckboxClick = (event, id) => {
    event.stopPropagation();
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
  };

  const handleRowClick = (event, leadId) => {
    // Prevent navigation when clicking on interactive elements
    if (event.target.closest('.MuiCheckbox-root') ||
        event.target.closest('.MuiIconButton-root') ||
        event.target.closest('.MuiButton-root')) {
      return;
    }

    if (event.ctrlKey || event.metaKey) {
      // Ctrl+click or Cmd+click - open in new tab
      window.open(`/leads/${leadId}`, '_blank');
    } else {
      // Regular click - navigate in current tab
      navigate(`/leads/${leadId}`);
    }
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const handleOpenCommentDialog = (lead) => {
    setSelectedLeadForComment(lead);
    setCommentDialogOpen(true);
    setNewComment('');
  };

  const handleCloseCommentDialog = () => {
    setCommentDialogOpen(false);
    setSelectedLeadForComment(null);
    setNewComment('');
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedLeadForComment) return;
    
    setCommentLoading(true);
    try {
      await onAddComment?.(selectedLeadForComment.id, {
        comment: newComment,
        is_internal: isInternal
      });
      handleCloseCommentDialog();
    } catch (error) {
      console.error('Failed to add comment:', error);
    } finally {
      setCommentLoading(false);
    }
  };

  const getLatestComment = (comments) => {
    if (!Array.isArray(comments) || comments.length === 0) return null;
    // Sort by created_at descending to get the most recent comment
    const sortedComments = [...comments].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return sortedComments[0];
  };

  const EnhancedTableToolbar = ({ numSelected }) => (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        ...(numSelected > 0 && {
          bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
        }),
      }}
    >
      {numSelected > 0 ? (
        <Typography
          sx={{ flex: '1 1 100%' }}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          {numSelected} selected
        </Typography>
      ) : (
        <Typography
          sx={{ flex: '1 1 100%' }}
          variant="h6"
          id="tableTitle"
          component="div"
        >
          Leads
        </Typography>
      )}

      {numSelected > 0 ? (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            startIcon={<Assignment />}
            onClick={() => onBulkAssign?.(selected)}
          >
            Assign
          </Button>
          <Button
            size="small"
            startIcon={<Delete />}
            color="error"
            onClick={() => {
              if (window.confirm('Are you sure you want to delete selected leads?')) {
                selected.forEach(id => onDeleteLead?.(id));
                setSelected([]);
              }
            }}
          >
            Delete
          </Button>
        </Box>
      ) : (
        <Button
          startIcon={<FileDownload />}
          onClick={() => onExportLeads?.()}
          size="small"
        >
          Export
        </Button>
      )}
    </Toolbar>
  );

  return (
    <Card>
      <CardContent>
        {/* Filters */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="Search leads..."
            value={searchTerm}
            onChange={handleSearchChange}
            size="small"
            sx={{ minWidth: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={handleStatusFilterChange}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="hot">Hot</MenuItem>
              <MenuItem value="warm">Warm</MenuItem>
              <MenuItem value="cold">Cold</MenuItem>
              <MenuItem value="dead">Dead</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Priority</InputLabel>
            <Select
              value={priorityFilter}
              label="Priority"
              onChange={handlePriorityFilterChange}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="urgent">Urgent</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="low">Low</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Source</InputLabel>
            <Select
              value={sourceFilter}
              label="Source"
              onChange={handleSourceFilterChange}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="google">Google Ads</MenuItem>
              <MenuItem value="meta">Meta/Facebook</MenuItem>
              <MenuItem value="manual">Manual</MenuItem>
              <MenuItem value="website">Website</MenuItem>
            </Select>
          </FormControl>

          <Button
            startIcon={<FilterList />}
            onClick={clearFilters}
            variant="outlined"
            size="small"
          >
            Clear Filters
          </Button>
        </Box>

        {/* Toolbar */}
        <EnhancedTableToolbar numSelected={selected.length} />

        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    indeterminate={selected.length > 0 && selected.length < leads.length}
                    checked={leads.length > 0 && selected.length === leads.length}
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>City</TableCell>
                <TableCell>Disease</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Assigned To</TableCell>
                <TableCell>Follow-up</TableCell>
                <TableCell>Last Comment</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={14} align="center">
                    <Typography>Loading...</Typography>
                  </TableCell>
                </TableRow>
              ) : leads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={14} align="center">
                    <Typography color="text.secondary">No leads found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                leads.map((lead) => {
                  const isItemSelected = isSelected(lead.id);
                  return (
                    <TableRow
                      key={lead.id}
                      hover
                      onClick={(event) => handleRowClick(event, lead.id)}
                      selected={isItemSelected}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        }
                      }}
                      title="Click to view details • Ctrl+Click to open in new tab"
                    >
                      <TableCell padding="checkbox" onClick={(event) => event.stopPropagation()}>
                        <Checkbox
                          color="primary"
                          checked={isItemSelected}
                          onChange={(event) => handleCheckboxClick(event, lead.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {lead.name}
                        </Typography>
                        {lead.title1 && (
                          <Typography variant="caption" color="text.secondary">
                            {lead.title1}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{lead.phone}</TableCell>
                      <TableCell>{lead.email || '-'}</TableCell>
                      <TableCell>{lead.city_name}</TableCell>
                      <TableCell>{lead.disease_name}</TableCell>
                      <TableCell>
                        <Chip
                          label={lead.status}
                          size="small"
                          color={getStatusColor(lead.status)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={lead.priority}
                          size="small"
                          color={getPriorityColor(lead.priority)}
                          variant="filled"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={lead.lead_source || 'manual'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {lead.assigned_to_name || (
                          <Typography variant="body2" color="text.secondary">
                            Unassigned
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {lead.follow_up_date ? (
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {new Date(lead.follow_up_date).toLocaleDateString()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(lead.follow_up_date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                            </Typography>
                            {(() => {
                              const now = new Date();
                              const followUpDate = new Date(lead.follow_up_date);
                              const diffDays = Math.ceil((followUpDate - now) / (1000 * 60 * 60 * 24));
                              
                              if (diffDays < 0) {
                                return (
                                  <Chip 
                                    label={`${Math.abs(diffDays)} days overdue`}
                                    size="small"
                                    color="error"
                                    variant="filled"
                                    sx={{ fontSize: '0.6rem', height: 16, mt: 0.5 }}
                                  />
                                );
                              } else if (diffDays === 0) {
                                return (
                                  <Chip 
                                    label="Due today"
                                    size="small"
                                    color="warning"
                                    variant="filled"
                                    sx={{ fontSize: '0.6rem', height: 16, mt: 0.5 }}
                                  />
                                );
                              } else if (diffDays <= 2) {
                                return (
                                  <Chip 
                                    label={`Due in ${diffDays} day${diffDays > 1 ? 's' : ''}`}
                                    size="small"
                                    color="info"
                                    variant="filled"
                                    sx={{ fontSize: '0.6rem', height: 16, mt: 0.5 }}
                                  />
                                );
                              }
                              return null;
                            })()}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No follow-up
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell onClick={(event) => event.stopPropagation()}>
                        {(() => {
                          const latestComment = getLatestComment(lead.comments);
                          return (
                            <Box sx={{ minWidth: 200, maxWidth: 300 }}>
                              {latestComment ? (
                                <Box>
                                  <Typography variant="body2" noWrap>
                                    {latestComment.comment}
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                    <Typography variant="caption" color="text.secondary">
                                      by {latestComment.user_name}
                                    </Typography>
                                    <Chip 
                                      label={latestComment.is_internal ? 'Internal' : 'Customer'} 
                                      size="small" 
                                      variant="outlined"
                                      color={latestComment.is_internal ? 'primary' : 'default'}
                                      sx={{ fontSize: '0.6rem', height: 16 }}
                                    />
                                  </Box>
                                  <Typography variant="caption" color="text.secondary">
                                    {new Date(latestComment.created_at).toLocaleDateString()}
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  No comments
                                </Typography>
                              )}
                              <Box sx={{ mt: 1 }}>
                                <Button
                                  size="small"
                                  startIcon={<Add />}
                                  onClick={() => handleOpenCommentDialog(lead)}
                                  variant="outlined"
                                >
                                  Add Comment
                                </Button>
                              </Box>
                            </Box>
                          );
                        })()} 
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {format(new Date(lead.created_at), 'MMM dd, yyyy')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {format(new Date(lead.created_at), 'HH:mm')}
                        </Typography>
                      </TableCell>
                      <TableCell onClick={(event) => event.stopPropagation()}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="View details • Ctrl+Click for new tab">
                            <IconButton
                              size="small"
                              onClick={(event) => {
                                if (event.ctrlKey || event.metaKey) {
                                  window.open(`/leads/${lead.id}`, '_blank');
                                } else {
                                  navigate(`/leads/${lead.id}`);
                                }
                              }}
                              color="primary"
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          <IconButton
                            size="small"
                            onClick={() => onCallLead?.(lead.phone, lead.id)}
                            color="success"
                          >
                            <Phone />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, lead)}
                          >
                            <MoreVert />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => onPageChange?.(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => onPageSizeChange?.(parseInt(e.target.value))}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={(event) => {
            if (event.ctrlKey || event.metaKey) {
              window.open(`/leads/${selectedLead?.id}`, '_blank');
            } else {
              navigate(`/leads/${selectedLead?.id}`);
            }
            handleMenuClose();
          }}>
            <Visibility sx={{ mr: 1 }} />
            View Details
          </MenuItem>
          <MenuItem onClick={() => {
            onEditLead?.(selectedLead);
            handleMenuClose();
          }}>
            <Edit sx={{ mr: 1 }} />
            Edit Lead
          </MenuItem>
          <MenuItem onClick={() => {
            onAssignLead?.(selectedLead);
            handleMenuClose();
          }}>
            <Assignment sx={{ mr: 1 }} />
            Assign Lead
          </MenuItem>
          <MenuItem onClick={() => {
            onCallLead?.(selectedLead?.phone, selectedLead?.id);
            handleMenuClose();
          }}>
            <Phone sx={{ mr: 1 }} />
            Call Lead
          </MenuItem>
          <MenuItem onClick={() => {
            onFollowUpLead?.(selectedLead);
            handleMenuClose();
          }}>
            <AccessTime sx={{ mr: 1 }} />
            Set Follow-up
          </MenuItem>
          <MenuItem onClick={() => {
            onScheduleCall?.(selectedLead);
            handleMenuClose();
          }}>
            <Schedule sx={{ mr: 1 }} />
            Schedule Call
          </MenuItem>
          <MenuItem onClick={() => {
            if (window.confirm('Are you sure you want to delete this lead?')) {
              onDeleteLead?.(selectedLead?.id);
            }
            handleMenuClose();
          }}>
            <Delete sx={{ mr: 1 }} />
            Delete Lead
          </MenuItem>
        </Menu>

        {/* Comment Dialog */}
        <Dialog 
          open={commentDialogOpen} 
          onClose={handleCloseCommentDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Add Comment - {selectedLeadForComment?.name}
            <Typography variant="body2" color="text.secondary">
              {selectedLeadForComment?.phone} • {selectedLeadForComment?.city_name}
            </Typography>
          </DialogTitle>
          <DialogContent>
            {/* Existing Comments */}
            {selectedLeadForComment?.comments && selectedLeadForComment.comments.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>Recent Comments</Typography>
                <List>
                  {selectedLeadForComment.comments.slice(-3).map((comment) => (
                    <ListItem key={comment.id} alignItems="flex-start">
                      <Avatar sx={{ width: 32, height: 32, mr: 2 }}>
                        {comment.user_name?.charAt(0)}
                      </Avatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight="bold">
                              {comment.user_name}
                            </Typography>
                            <Chip
                              label={comment.is_internal ? 'Internal' : 'Customer'}
                              size="small"
                              color={comment.is_internal ? 'primary' : 'default'}
                              variant="outlined"
                            />
                            <Typography variant="caption" color="text.secondary">
                              {new Date(comment.created_at).toLocaleString()}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {comment.comment}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* New Comment Form */}
            <Box>
              <Typography variant="h6" gutterBottom>Add New Comment</Typography>
              
              <Box sx={{ mb: 2 }}>
                <Button
                  variant={isInternal ? 'contained' : 'outlined'}
                  onClick={() => setIsInternal(true)}
                  size="small"
                  sx={{ mr: 1 }}
                >
                  Internal
                </Button>
                <Button
                  variant={!isInternal ? 'contained' : 'outlined'}
                  onClick={() => setIsInternal(false)}
                  size="small"
                >
                  Customer Facing
                </Button>
              </Box>

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Comment"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Enter your comment..."
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCommentDialog} disabled={commentLoading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleAddComment}
              disabled={!newComment.trim() || commentLoading}
              startIcon={<Send />}
            >
              {commentLoading ? 'Adding...' : 'Add Comment'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default LeadsTable;